/**
 * TIMER V2 — 8G.2C.11: ONE PLACE THAT UNDERSTANDS THE EDGE WIRE CONTRACT.
 *
 * `supabase/functions/_shared/edgeResponse.ts` wraps every success payload:
 *
 *     { success: true, ok: true, data: <payload> }
 *
 * and `supabase.functions.invoke` hands that entire body back as its OWN
 * `.data`. So a caller sees two levels:
 *
 *     invoke.data.data.outcome        <- the payload field
 *     invoke.data.outcome             <- nothing
 *
 * 8G.2C.10 read the second one. Its unit tests passed anyway because the
 * FIXTURE encoded the same mistake — a flat `{ data: { outcome } }` body that
 * this endpoint has never emitted. A test and an implementation sharing one
 * wrong assumption agree perfectly with each other and with nothing else.
 *
 * CAPTURED from a real local Edge invocation (not a mock):
 *
 *   {"success":true,"ok":true,"data":{"outcome":"CREATED_QUEUED","created":true,
 *    "status":"queued","sendAt":"2026-08-28T10:00:00+00:00","queued":1, ...}}
 *   {"success":true,"ok":true,"data":{"message":"Success","canceled":0}}
 *
 * Every classifier now goes through here, so no caller re-learns the shape and
 * none of them can disagree about it.
 */

/** What `supabase.functions.invoke` resolves with. */
export interface InvokeLike {
  data?: unknown;
  error?: unknown;
}

export type EnvelopeKind =
  /** The invoke itself failed, or the function returned a Tier-II error. */
  | 'TRANSPORT_ERROR'
  /** A canonical `{ success: true, ok: true, data: {...} }` body. */
  | 'OK'
  /** Something else. Never treated as success. */
  | 'MALFORMED';

export interface Envelope {
  kind: EnvelopeKind;
  /** The INNER payload — `body.data` — present only when kind === 'OK'. */
  payload: Record<string, unknown> | null;
  detail: string | null;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function describeError(err: unknown): string | null {
  if (!err) return null;
  if (typeof err === 'string') return err;
  const m = (err as { message?: unknown }).message;
  return typeof m === 'string' ? m : null;
}

/**
 * Normalize one invoke result.
 *
 * Canonical success requires ALL of: no invoke error, an object body,
 * `success === true`, `ok === true`, and an object `data`. Anything else is
 * MALFORMED and fails closed.
 *
 * Contradictory flags (`success: true, ok: false` and the reverse) are
 * explicitly NOT accepted. A body that half-claims success is a body whose
 * producer and consumer disagree, which is the exact class of defect this file
 * exists to end — and a nested field that looks familiar is not a reason to
 * trust the rest of it.
 */
export function readEdgeEnvelope(res: InvokeLike | null | undefined): Envelope {
  // `functions.invoke` RESOLVES with `{ error }` on failure rather than
  // rejecting, so a `.catch()` never runs and `!res.error` was, for two
  // phases, the only thing standing between a dead call and a success claim.
  if (!res) return { kind: 'TRANSPORT_ERROR', payload: null, detail: 'no result' };
  if (res.error) {
    return { kind: 'TRANSPORT_ERROR', payload: null, detail: describeError(res.error) };
  }

  const body = res.data;
  if (!isObject(body)) {
    return { kind: 'MALFORMED', payload: null, detail: 'no response body' };
  }

  // The old flat shape — `{ queued: 1 }` with no envelope at all — reaches
  // here and is correctly rejected: it carries no evidence about the row.
  if (body.success !== true || body.ok !== true) {
    return {
      kind: 'MALFORMED',
      payload: null,
      detail: body.success === true || body.ok === true
        ? 'contradictory success flags'
        : 'not a canonical success envelope',
    };
  }

  if (!isObject(body.data)) {
    return { kind: 'MALFORMED', payload: null, detail: 'envelope carries no payload' };
  }

  return { kind: 'OK', payload: body.data, detail: null };
}
